<script setup lang="ts">
import { ref } from 'vue';
import { TextInput } from '@hantera/design-system';
import useBackend from '../../composition/useBackend';
import PermissionPicker from '../Iam/Shared/PermissionPicker.vue';

const emit = defineEmits<{
  (event: 'cancelled'): void
  (event: 'created', accessToken: string): void
}>()

const { user } = useBackend();

const description = ref('');
const descriptionInput = ref<InstanceType<typeof TextInput>>();

const expiresAt = ref('');
const permissionError = ref('');

const permissions = ref<string[]>([]);
const searchQuery = ref('');

async function submit() {
  if (!description.value.trim()) {
    descriptionInput.value?.setError('Please provide a name for your token');
    return;
  }

  if (permissions.value.length === 0) {
    permissionError.value = 'Please select at least one permission';
    return;
  }

  permissionError.value = '';

  const response = await user.createPersonalAccessToken(
    description.value,
    expiresAt.value,
    permissions.value.join(' ')
  );

  if (response) {
    emit('created', response.accessToken)
  }
}

let minValidUntil = new Date();
minValidUntil.setDate(minValidUntil.getDate() + 1);
minValidUntil = new Date(minValidUntil.toISOString().split('T')[0]);

let maxValidUntil = new Date();
maxValidUntil.setFullYear(maxValidUntil.getFullYear() + 1);
maxValidUntil = new Date(maxValidUntil.toISOString().split('T')[0]);

</script>

<template>

<h2>Create New Personal Access Tokens (PAT)</h2>

<label class="create-pat__input">
  Give your token a name to help you identify it later:
  <TextInput v-model="description" placeholder="My token" ref="descriptionInput" />
</label>

<label class="create-pat__input">
  Valid until (max 1 year):
  <input type="date" :max="maxValidUntil.toDateString()" :min="minValidUntil.toDateString()" v-model="expiresAt"/>
</label>

<h3>Permissions</h3>

<input
  v-model="searchQuery"
  type="text"
  placeholder="Search permissions..."
  class="input create-pat__search"
/>

<PermissionPicker v-model="permissions" v-model:search-query="searchQuery" />

  <div class="actions">
    <button class="btn-primary" @click="submit()">Create PAT</button>
    <button class="btn-link" @click="emit('cancelled')">Cancel</button>
  </div>

  <div v-if="permissionError" class="permission-error">{{ permissionError }}</div>

</template>

<style scoped>
.actions {
  display: flex;
  flex-direction: row;
  gap: 10px;
  align-items: center;
  margin-top: 10px;
}
</style>

<style>
.create-pat__input input {
  max-width: 250px;
  margin-top: .5rem;
}

.create-pat__search {
  margin-bottom: 1rem;
}

.permission-error {
  margin-top: 1rem;
  color: var(--color-error);
}
</style>
